import { Location } from 'history';
import { DialogsState } from '../reducers/types';
import { BucketObject } from '../services/storage/types';
import { Apartment, BeforeLoginApartment } from './apartments';
import { Application, PartnerLead } from './applications';
import { ImageFileWithObjectURL } from './files';
import { Showing, FormShowing, ReservedShowing, ReservableShowings, ReserveData } from './showings';
import { TenantProfileState } from './tenantProfile';
import { User, CognitoUserData } from './users';
// apartments
export interface ApartmentIdIndex<ListItem> {
    [apartmentId: string]: ListItem;
}
export type IndexedApartments = ApartmentIdIndex<Apartment>;
export interface ApartmentsState {
    apartments: ApartmentIdIndex<Apartment> | ApartmentIdIndex<never>; // indexed by apartment id
    files: ApartmentIdIndex<BucketObject[]> | ApartmentIdIndex<never>; // indexed by apartment id
    beforeLoginApartment: BeforeLoginApartment | undefined;
    beforeLoginMedia: ImageFileWithObjectURL[];
    isApartmentsSearchLoading: boolean;
    publicApartment: Apartment | null;
    isApartmentsLoading: boolean;
    selectedApartmentId?: string;
}
// router
export interface LocationInterface extends Location {
    query: {
        [key: string]: any;
    };
}
export interface RouterState {
    location: LocationInterface;
}
// Applications
export type IndexedApplications = ApartmentIdIndex<Application[]> | ApartmentIdIndex<never>;
export interface ApplicationsState {
    applications: IndexedApplications; // indexed by apartment id
    createApplication: {
        application: Application | null;
        error: string | null;
        loading: boolean;
    };
    getApartmentLead: {
        lead: PartnerLead | null;
        error: string | null;
        loading: boolean;
    };
    getConversation: {
        conversation: any | null;
        error: string | null;
        loading: boolean;
    };
    isApplicationsLoading: boolean;
    selectedApplications: string[];
}
// Users
export interface StateUserAttributes {
    sub: string;
    email_verified: true;
    name: string;
    phone_number_verified: false;
    phone_number: string;
    email: string;
    collateralVerified?: boolean;
}
export interface Device {
    isMobile?: boolean;
    windowWidth?: string;
}
export interface UserIdIndex {
    [userId: string]: User;
}
export type IndexedUsersState = UserIdIndex | Record<string, never>;
export interface UsersState {
    sessionInitializationAttempted: boolean;
    isLoadingSession: boolean;
    notConfirmedEmail: string | null;
    lastUrl?: string;
    /** Data from cog */
    cognitoData: CognitoUserData;
    users: IndexedUsersState;
    areUsersLoading: boolean;
    device: Device;
    isPasswordResetCodeSent: boolean;
}
// Lists
export interface NestedListInterface {
    id: string;
    isOpen: boolean;
}
export interface ListsState {
    nestedLists: {
        [apartmentId: string]: NestedListInterface;
    } | Record<string, never>;
}
export interface ReserveViewLogic {
    bestReservableIndex: number;
    bestReservableAmount: number;
    loopedThrough: boolean;
    reservableIndex: number;
    reservableAmount: number;
    error?: string;
}
export interface ShowingsInterface {
    /** can also be old showings */
    showings: Showing[];
    reservableShowings: ReservableShowings;
    /** can also be old reservations */
    reservations: ReservedShowing[];
}
export const EmptyReservableShowings: ReservableShowings = {
    reservables: [],
    bestReservables: []
};
export const EmptyShowingsInterface: ShowingsInterface = {
    showings: [],
    reservableShowings: EmptyReservableShowings,
    reservations: []
};
export interface ReserveInterface {
    reserveData?: ReserveData;
    // using ReserveViewLogic object so it's easier to update state
    reserveViewLogic: ReserveViewLogic;
    loading: boolean;
}
export type IndexedShowingsInterfaces = ApartmentIdIndex<ShowingsInterface> | ApartmentIdIndex<never>;
export interface OpenShowing {
    id: string;
    apartmentId: string;
    startDate: string;
    endDate: string;
    type: 'openHouse';
    createdAt: number;
}
export interface ShowingsState {
    showingsInterfaces: IndexedShowingsInterfaces;
    openShowings: ApartmentIdIndex<OpenShowing[]>;
    reserve: ReserveInterface;
    loading: boolean;
    error: string | null;
}
export interface CreateShowingsFormState {
    showings?: FormShowing[];
    confirmImmediately?: boolean;
    selectedApplication?: string[];
}
export interface ApplicantAvailabilitiesState {
    isCreating: boolean;
    isDeleting: boolean;
    isFetchingByApplicants: boolean;
    availabilities: [
    ];
    availabilitiesByApplicants: [
    ];
}
// MainState
export interface StateInterface {
    apartments: ApartmentsState;
    applications: ApplicationsState;
    router: RouterState;
    users: UsersState;
    lists: ListsState;
    showings: ShowingsState;
    dialogs: DialogsState;
    form?: any;
    availabilities: ApplicantAvailabilitiesState;
    tenantProfile: TenantProfileState;
}
